// ═══════════════════════════════════════════════════════════════════
//  TOQUM · COMMERCIAL DECISIONS (CD) MODULE
//
//  Flow:
//  Inspection fails system thresholds
//  → QA puts report On Hold → raises CD request
//  → Manager reviews → Approves OR escalates to Merchant
//  → Merchant sends commercial approval
//  → Manager connects approval → marks Passed (CD)
//  → Inspection report flagged as commercially approved
//  → Summary page: date / supplier / factory / QA / manager / merchant wise
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import { T, uid, today, num, Section, Field, Grid, Btn, DataTable,
  TabBar, Badge, StatCard, Collapse, Modal, Toast, isManager, API_URL } from './Shared';

// ── Status colour map ─────────────────────────────────────────────
const CD_STATUS = {
  'On Hold'              : { color:'#64748b', bg:'#f1f5f9' },
  'Pending QA Manager'   : { color:'#f59e0b', bg:'#fffbeb' },
  'Escalated to Merchant': { color:'#8b5cf6', bg:'#f5f3ff' },
  'Merchant Approved'    : { color:'#0891b2', bg:'#ecfeff' },
  'Passed (CD)'          : { color:'#059669', bg:'#ecfdf5' },
  'Rejected'             : { color:'#dc2626', bg:'#fef2f2' },
};

const CDPill = ({ status }) => {
  const s = CD_STATUS[status] || { color:'#64748b', bg:'#f1f5f9' };
  return (
    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:10.5,
      fontWeight:800, background:s.bg, color:s.color, whiteSpace:'nowrap' }}>
      {status}
    </span>
  );
};

// ── Reason categories for CD requests ─────────────────────────────
const CD_REASONS = [
  'AQL failure — critical defects within commercial tolerance',
  'Measurement deviation — commercial decision to ship',
  'Minor workmanship — buyer approved after negotiation',
  'Fabric defects — order partially accepted',
  'Packing non-conformance — corrected at destination',
  'Colour variation — buyer commercial decision',
  'Late delivery risk — ship vs re-inspect',
  'Other commercial decision',
];

// ── Threshold definitions (editable by admin) ─────────────────────
const DEFAULT_THRESHOLDS = {
  criticalDefects: 0,      // any critical = auto fail
  majorPct:        2.5,    // >2.5% major defect rate
  minorPct:        4.0,    // >4.0% minor defect rate
  measFailPct:     20,     // >20% measurement points failing
  dhu:             5.0,    // DHU above this = fail
};

// ═══════════════════════════════════════════════════════════════════
// CD REQUEST FORM (raised by QA from inspection form)
// ═══════════════════════════════════════════════════════════════════
export function RaiseCDRequest({ inspection, currentUser, db, setDb, saveToDB, onClose, onSuccess }) {
  const [f, setF] = useState({
    reason:     '',
    detail:     '',
    orderValue: '',
    currency:   'INR',
    qty:        inspection?.header?.ordQty || '',
    urgency:    'High',
    merchantName: '',
    buyerContact: '',
    notes:      '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.reason || !f.detail) return alert('Reason and detail are required');
    setSaving(true);

    const cd = {
      id:            uid(),
      type:          'CommercialDecision',
      inspectionId:  inspection?.id,
      poNo:          inspection?.poNo,
      styleNo:       inspection?.styleNo,
      supplier:      inspection?.supplier,
      brand:         inspection?.brand,
      stage:         inspection?.stage,
      qaName:        currentUser.name,
      qaMember:      currentUser.name,
      raisedOn:      today(),
      raisedAt:      new Date().toISOString(),
      status:        'Pending QA Manager',
      reason:        f.reason,
      detail:        f.detail,
      orderValue:    f.orderValue,
      currency:      f.currency,
      qty:           f.qty,
      urgency:       f.urgency,
      merchantName:  f.merchantName,
      buyerContact:  f.buyerContact,
      notes:         f.notes,
      // Inspection failure details
      inspFails: {
        criticalDefects: inspection?.totals?.critical || 0,
        majorDefects:    inspection?.totals?.major    || 0,
        minorDefects:    inspection?.totals?.minor    || 0,
        measFails:       inspection?.measSummary?.fail || 0,
        result:          inspection?.result || 'Failed',
      },
      timeline: [{ action:'Raised', by:currentUser.name, at:new Date().toISOString(), note:f.detail }],
    };

    setDb(p=>({ ...p, commercialDecisions:[...(p.commercialDecisions||[]), cd] }));
    await saveToDB('commercialDecision', cd);

    // Update the inspection report to On Hold
    if (inspection?.id) {
      const updInsp = { ...inspection, status:'On Hold', cdId:cd.id };
      setDb(p=>({ ...p, inspections:(p.inspections||[]).map(i=>i.id===inspection.id?updInsp:i) }));
      await saveToDB('inspection', updInsp);
    }

    setSaving(false);
    onSuccess?.(cd);
    onClose?.();
  };

  const insp = inspection || {};
  return (
    <div style={{ fontFamily:"'Inter',sans-serif" }}>
      {/* Inspection failure summary */}
      <div style={{ background:'#fef2f2', border:'1.5px solid #fca5a5',
        borderRadius:10, padding:'12px 14px', marginBottom:16 }}>
        <div style={{ fontWeight:800, color:'#dc2626', fontSize:13, marginBottom:8 }}>
          ⚠ Inspection Failed — Commercial Decision Required
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:8 }}>
          {[
            ['PO', insp.poNo],
            ['Style', insp.styleNo],
            ['Supplier', insp.supplier],
            ['Stage', insp.stage],
            ['Critical', insp.totals?.critical || 0],
            ['Major', insp.totals?.major || 0],
            ['Minor', insp.totals?.minor || 0],
            ['Meas Fails', insp.measSummary?.fail || 0],
          ].map(([l,v])=>(
            <div key={l} style={{ background:'white', borderRadius:6, padding:'6px 8px' }}>
              <div style={{ fontSize:9, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>{l}</div>
              <div style={{ fontSize:13, fontWeight:800, color:'#1a1d2e' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
        <div>
          <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
            Reason for CD Request *
          </label>
          <select value={f.reason} onChange={e=>setF(p=>({...p,reason:e.target.value}))}
            style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`,
              borderRadius:7, fontSize:12.5, outline:'none' }}>
            <option value="">— Select reason —</option>
            {CD_REASONS.map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
            Urgency
          </label>
          <select value={f.urgency} onChange={e=>setF(p=>({...p,urgency:e.target.value}))}
            style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`,
              borderRadius:7, fontSize:12.5, outline:'none' }}>
            {['Critical — Ship Today','High','Medium','Low'].map(u=><option key={u}>{u}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom:12 }}>
        <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
          textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
          Detail / Justification *
        </label>
        <textarea value={f.detail} rows={3}
          onChange={e=>setF(p=>({...p,detail:e.target.value}))}
          placeholder="Explain why this order should be approved despite inspection failure. Include risk assessment, buyer negotiation status, defect acceptability etc."
          style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`,
            borderRadius:7, fontSize:12.5, resize:'vertical', boxSizing:'border-box' }}/>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
        <div>
          <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
            Order Value
          </label>
          <div style={{ display:'flex', gap:6 }}>
            <select value={f.currency} onChange={e=>setF(p=>({...p,currency:e.target.value}))}
              style={{ width:70, padding:'8px 6px', border:`1.5px solid ${T.border}`, borderRadius:7, fontSize:12 }}>
              {['INR','USD','GBP','EUR','AED'].map(c=><option key={c}>{c}</option>)}
            </select>
            <input type="number" value={f.orderValue} placeholder="e.g. 245000"
              onChange={e=>setF(p=>({...p,orderValue:e.target.value}))}
              style={{ flex:1, padding:'8px 10px', border:`1.5px solid ${T.border}`, borderRadius:7, fontSize:12.5 }}/>
          </div>
        </div>
        <div>
          <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
            Qty to Ship
          </label>
          <input type="number" value={f.qty}
            onChange={e=>setF(p=>({...p,qty:e.target.value}))}
            style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`, borderRadius:7, fontSize:12.5 }}/>
        </div>
        <div>
          <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
            Merchant Name
          </label>
          <input value={f.merchantName} placeholder="Who handles this buyer?"
            onChange={e=>setF(p=>({...p,merchantName:e.target.value}))}
            style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`, borderRadius:7, fontSize:12.5 }}/>
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
          textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
          Additional Notes / Supporting Info
        </label>
        <textarea value={f.notes} rows={2}
          onChange={e=>setF(p=>({...p,notes:e.target.value}))}
          placeholder="e.g. Buyer has already inspected samples and accepted the quality. Defects are in non-critical areas."
          style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`,
            borderRadius:7, fontSize:12.5, resize:'vertical', boxSizing:'border-box' }}/>
      </div>

      <div style={{ display:'flex', gap:10 }}>
        <Btn label={saving?'Raising…':'🚨 Put on Hold & Request CD Approval'}
          color={T.danger} disabled={saving} onClick={submit}/>
        {onClose && <Btn label="Cancel" outline color={T.textSub} onClick={onClose}/>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN CD MODULE PAGE
// ═══════════════════════════════════════════════════════════════════
export default function CommercialDecisions({ db, setDb, saveToDB, currentUser }) {
  const [tab, setTab]           = useState('PENDING');
  const [selCD, setSelCD]       = useState(null);
  const [merchantModal, setMerchantModal] = useState(null);
  const [rejectModal, setRejectModal]     = useState(null);
  const [toast, setToast]       = useState(null);
  const [thresholdModal, setThresholdModal] = useState(false);
  const [thresholds, setThresholds] = useState(
    db.cdThresholds || DEFAULT_THRESHOLDS
  );

  const notify = (msg, type='success') => {
    setToast({msg,type}); setTimeout(()=>setToast(null),3500);
  };

  const allCDs = db.commercialDecisions || [];

  // Also pull in process flow exceptions (gate exceptions) — show unified
  const gateExceptions = (db.exceptions||[]).filter(e =>
    e.type === 'Process Flow Exception'
  ).map(e => ({
    ...e,
    _isGateException: true,
    status: e.status === 'Pending Approval' ? 'Pending QA Manager' : e.status,
    reason: 'Process flow exception — ' + e.detail,
    qaName: e.raisedBy,
    raisedOn: e.raisedOn,
  }));

  const allItems = [...allCDs, ...gateExceptions];
  const pending   = allItems.filter(c=>['Pending QA Manager','On Hold','Pending Approval'].includes(c.status));
  const escalated = allItems.filter(c=>c.status==='Escalated to Merchant');
  const approved  = allItems.filter(c=>['Passed (CD)','Approved'].includes(c.status));
  const rejected  = allItems.filter(c=>c.status==='Rejected');

  // ── Update CD status with timeline entry ──────────────────────
  const updateCD = async (cd, status, extra={}, timelineNote='') => {
    const entry = { action:status, by:currentUser.name,
      at:new Date().toISOString(), note:timelineNote };
    const upd = { ...cd, ...extra, status,
      timeline:[...(cd.timeline||[]), entry] };
    setDb(p=>({ ...p, commercialDecisions:
      (p.commercialDecisions||[]).map(c=>c.id===upd.id?upd:c) }));
    await saveToDB('commercialDecision', upd);

    // If Passed (CD) — update the linked inspection report
    if (status==='Passed (CD)' && cd.inspectionId) {
      const insp = (db.inspections||[]).find(i=>i.id===cd.inspectionId);
      if (insp) {
        const updInsp = { ...insp, status:'Passed (CD)',
          cdApproval:{ cdId:cd.id, approvedBy:currentUser.name,
            merchantName:upd.merchantName, approvedAt:new Date().toISOString(),
            reason:upd.reason, notes:upd.merchantApprovalNotes||upd.notes } };
        setDb(p=>({ ...p, inspections:
          (p.inspections||[]).map(i=>i.id===insp.id?updInsp:i) }));
        await saveToDB('inspection', updInsp);
      }
    }
    setSelCD(null);
    notify(`Status updated: ${status}`);
  };

  // ── Save thresholds ───────────────────────────────────────────
  const saveThresholds = async () => {
    setDb(p=>({ ...p, cdThresholds:thresholds }));
    await saveToDB('cdThresholds', { id:'cdThresholds', ...thresholds });
    setThresholdModal(false);
    notify('Thresholds saved');
  };

  // ── CD Detail panel ───────────────────────────────────────────
  const CDDetail = ({ cd }) => {
    const [merchantNote, setMerchantNote]   = useState(cd.merchantApprovalNotes||'');
    const [merchantRef,  setMerchantRef]    = useState(cd.merchantRef||'');
    const [rejectReason, setRejectReason]   = useState('');
    const [saving, setSaving]               = useState(false);

    return (
      <div style={{ background:'white', border:`1px solid ${T.border}`,
        borderRadius:12, overflow:'hidden', marginBottom:16 }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#0a0e1a,#1a2236)',
          padding:'14px 18px', color:'white' }}>
          <div style={{ display:'flex', justifyContent:'space-between',
            alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
            <div>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
                fontSize:15, fontWeight:900 }}>
                CD-{cd.id.slice(-6).toUpperCase()} · PO {cd.poNo}
              </div>
              <div style={{ fontSize:11.5, opacity:0.7, marginTop:2 }}>
                {cd.supplier} · {cd.brand} · {cd.stage} · Raised by {cd.qaName} on {cd.raisedOn}
              </div>
            </div>
            <CDPill status={cd.status}/>
          </div>
        </div>

        <div style={{ padding:'14px 18px' }}>
          {/* KPI row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))',
            gap:10, marginBottom:14 }}>
            {[
              ['Style', cd.styleNo],
              ['Urgency', cd.urgency],
              ['Qty', num(cd.qty).toLocaleString()+' pcs'],
              ['Order Value', cd.currency+' '+(parseFloat(cd.orderValue)||0).toLocaleString()],
              ['Merchant', cd.merchantName||'—'],
              ['Critical Defects', cd.inspFails?.criticalDefects||0],
              ['Major Defects', cd.inspFails?.majorDefects||0],
              ['Meas Fails', cd.inspFails?.measFails||0],
            ].map(([l,v])=>(
              <div key={l} style={{ background:'#f8fafc', borderRadius:8, padding:'8px 10px' }}>
                <div style={{ fontSize:9.5, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em' }}>{l}</div>
                <div style={{ fontSize:12.5, fontWeight:800, color:'#0a0e1a', marginTop:2 }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Reason */}
          <div style={{ background:'#fffbeb', border:'1px solid #fde68a',
            borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
            <div style={{ fontSize:10.5, fontWeight:700, color:'#92400e',
              textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>
              Reason for Commercial Decision
            </div>
            <div style={{ fontSize:13, fontWeight:700, color:'#78350f', marginBottom:4 }}>{cd.reason}</div>
            <div style={{ fontSize:12.5, color:'#92400e', lineHeight:1.6 }}>{cd.detail}</div>
            {cd.notes && <div style={{ fontSize:11.5, color:'#a16207', marginTop:6,
              borderTop:'1px solid #fde68a', paddingTop:6 }}>{cd.notes}</div>}
          </div>

          {/* Manager actions */}
          {isManager(currentUser) && cd.status==='Pending QA Manager' && (
            <div style={{ background:'#f0fdf4', border:'1px solid #86efac',
              borderRadius:8, padding:'12px 14px', marginBottom:12 }}>
              <div style={{ fontWeight:800, fontSize:12, color:'#065f46', marginBottom:10 }}>
                QA Manager Decision
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <Btn label="✓ Approve Directly"
                  color={T.success} small
                  onClick={()=>updateCD(cd,'Passed (CD)',{},'Manager approved directly')}/>
                <Btn label="↑ Escalate to Merchant"
                  color={T.purple} small
                  onClick={()=>updateCD(cd,'Escalated to Merchant',{},
                    'Escalated to merchant for commercial approval')}/>
                <Btn label="✕ Reject — Re-inspect"
                  color={T.danger} small
                  onClick={()=>setRejectModal(cd)}/>
              </div>
            </div>
          )}

          {/* Merchant approval input */}
          {isManager(currentUser) && cd.status==='Escalated to Merchant' && (
            <div style={{ background:'#f5f3ff', border:'1px solid #c4b5fd',
              borderRadius:8, padding:'12px 14px', marginBottom:12 }}>
              <div style={{ fontWeight:800, fontSize:12, color:'#4c1d95', marginBottom:10 }}>
                Connect Merchant Commercial Approval
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div>
                  <label style={{ fontSize:10.5, fontWeight:700, color:'#6d28d9',
                    display:'block', marginBottom:3 }}>Merchant Approval Ref / Email Subject</label>
                  <input value={merchantRef} onChange={e=>setMerchantRef(e.target.value)}
                    placeholder="e.g. RE: Commercial approval PO-2026-441"
                    style={{ width:'100%', padding:'8px 10px', border:'1.5px solid #c4b5fd',
                      borderRadius:7, fontSize:12.5 }}/>
                </div>
                <div>
                  <label style={{ fontSize:10.5, fontWeight:700, color:'#6d28d9',
                    display:'block', marginBottom:3 }}>Merchant Name</label>
                  <input value={merchantNote} onChange={e=>setMerchantNote(e.target.value)}
                    placeholder="Merchant / buyer contact name"
                    style={{ width:'100%', padding:'8px 10px', border:'1.5px solid #c4b5fd',
                      borderRadius:7, fontSize:12.5 }}/>
                </div>
              </div>
              <Btn label={saving?'Saving…':'✓ Merchant Approved — Mark Passed (CD)'}
                color={T.success} small disabled={saving}
                onClick={async()=>{
                  setSaving(true);
                  await updateCD(cd,'Passed (CD)',{
                    merchantRef, merchantApprovalNotes:merchantNote,
                  },'Merchant approval connected by '+currentUser.name);
                  setSaving(false);
                }}/>
            </div>
          )}

          {/* Timeline */}
          <Collapse title="Approval Timeline" defaultOpen>
            <div style={{ display:'flex', flexDirection:'column', gap:0, paddingLeft:8 }}>
              {(cd.timeline||[]).map((t,i)=>(
                <div key={i} style={{ display:'flex', gap:12,
                  paddingBottom:12, position:'relative' }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
                    <div style={{ width:10, height:10, borderRadius:'50%',
                      background:i===0?T.primary:T.success, marginTop:3 }}/>
                    {i<(cd.timeline||[]).length-1 && (
                      <div style={{ width:2, flex:1, background:'#e2e8f0', marginTop:2 }}/>
                    )}
                  </div>
                  <div style={{ paddingBottom:4 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#0a0e1a' }}>{t.action}</div>
                    <div style={{ fontSize:11, color:T.textSub }}>
                      {t.by} · {t.at?.split('T')[0]} {t.at?.split('T')[1]?.slice(0,5)}
                    </div>
                    {t.note && <div style={{ fontSize:11.5, color:'#475569', marginTop:2 }}>{t.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      </div>
    );
  };

  // ── CD Summary (analytics page) ───────────────────────────────
  const CDSummary = () => {
    const bySupplier = {};
    const byQA       = {};
    const byMerchant = {};
    const byMonth    = {};

    approved.forEach(cd=>{
      const m = cd.raisedOn?.slice(0,7)||'—';
      bySupplier[cd.supplier] = (bySupplier[cd.supplier]||0)+1;
      byQA[cd.qaName]         = (byQA[cd.qaName]||0)+1;
      byMerchant[cd.merchantName||'—'] = (byMerchant[cd.merchantName||'—']||0)+1;
      byMonth[m]              = (byMonth[m]||0)+1;
    });

    const totalValue = approved.reduce((a,c)=>
      a+(c.currency==='INR'?1:c.currency==='USD'?84:c.currency==='GBP'?108:1)
      *parseFloat(c.orderValue||0),0);

    const SummaryTable = ({ data, label }) => (
      <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12 }}>
        <thead><tr style={{ background:'#f8fafc' }}>
          <th style={{ padding:'7px 10px', textAlign:'left', fontWeight:800,
            borderBottom:'2px solid #eef0f6', fontSize:10.5, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</th>
          <th style={{ padding:'7px 10px', textAlign:'right', fontWeight:800,
            borderBottom:'2px solid #eef0f6', fontSize:10.5, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.07em' }}>CDs Passed</th>
        </tr></thead>
        <tbody>{Object.entries(data)
          .sort((a,b)=>b[1]-a[1]).map(([k,v])=>(
          <tr key={k} style={{ borderBottom:`1px solid ${T.border}` }}>
            <td style={{ padding:'7px 10px', fontWeight:600 }}>{k}</td>
            <td style={{ padding:'7px 10px', textAlign:'right', fontWeight:800,
              color:T.primary }}>{v}</td>
          </tr>
        ))}</tbody>
      </table>
    );

    return (
      <div>
        {/* KPI cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
          gap:12, marginBottom:20 }}>
          <StatCard label="CD Requests" value={allCDs.length} color={T.primary}/>
          <StatCard label="Gate Exceptions" value={gateExceptions.length} color={T.warning}/>
          <StatCard label="Passed (CD)" value={approved.length} color={T.success}/>
          <StatCard label="Pending" value={pending.length} color={T.danger}/>
          <StatCard label="Escalated" value={escalated.length} color='#8b5cf6'/>
          <StatCard label="Rejected" value={rejected.length} color={T.danger}/>
          <StatCard label="Order Value"
            value={'₹'+(totalValue/100000).toFixed(1)+'L'} color={T.teal}/>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <Section title="By Supplier">
            <SummaryTable data={bySupplier} label="Supplier"/>
          </Section>
          <Section title="By QA Member">
            <SummaryTable data={byQA} label="QA Member"/>
          </Section>
          <Section title="By Merchant">
            <SummaryTable data={byMerchant} label="Merchant"/>
          </Section>
          <Section title="By Month">
            <SummaryTable data={byMonth} label="Month"/>
          </Section>
        </div>

        {/* Full table */}
        <Section title="All Commercially Approved Orders">
          <DataTable
            columns={['Date','PO','Style','Supplier','Brand','Stage','Reason',
              'Qty','Value','QA','Manager','Merchant','Status']}
            rows={[...allCDs].sort((a,b)=>(b.raisedOn||'').localeCompare(a.raisedOn||''))}
            renderCell={(r,c)=>({
              Date:     r.raisedOn,
              PO:       <b>{r.poNo}</b>,
              Style:    r.styleNo,
              Supplier: r.supplier,
              Brand:    r.brand,
              Stage:    r.stage,
              Reason:   <span style={{fontSize:11}}>{r.reason?.split('—')[0]}</span>,
              Qty:      num(r.qty).toLocaleString(),
              Value:    r.orderValue?`${r.currency} ${parseFloat(r.orderValue).toLocaleString()}`:'—',
              QA:       r.qaName,
              Manager:  r.timeline?.find(t=>['Passed (CD)','Escalated to Merchant'].includes(t.action))?.by||'—',
              Merchant: r.merchantName||'—',
              Status:   <CDPill status={r.status}/>,
            }[c])}
          />
        </Section>
      </div>
    );
  };

  // ── Thresholds modal ─────────────────────────────────────────
  const ThresholdModal = () => (
    <Modal title="CD Trigger Thresholds — When to Put on Hold"
      onClose={()=>setThresholdModal(false)}
      actions={<Btn label="Save Thresholds" color={T.success} onClick={saveThresholds}/>}>
      <div style={{ fontSize:12.5, color:T.textSub, marginBottom:14 }}>
        Define when an inspection report must be put On Hold and requires commercial approval.
        These thresholds override manual QA decisions.
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        {[
          ['Critical Defects (any = fail)', 'criticalDefects', 'Count — 0 means any critical triggers CD'],
          ['Major Defect Rate %', 'majorPct', 'e.g. 2.5 = above 2.5% major rate triggers CD'],
          ['Minor Defect Rate %', 'minorPct', 'e.g. 4.0 = above 4.0% minor rate triggers CD'],
          ['Measurement Fail %', 'measFailPct', 'e.g. 20 = >20% of POMs failing triggers CD'],
          ['DHU Threshold', 'dhu', 'e.g. 5.0 = DHU above 5.0 triggers CD'],
        ].map(([label, key, hint])=>(
          <div key={key}>
            <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
              textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:3 }}>
              {label}
            </label>
            <input type="number" step="0.1" value={thresholds[key]||0}
              onChange={e=>setThresholds(p=>({...p,[key]:parseFloat(e.target.value)||0}))}
              style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`,
                borderRadius:7, fontSize:13 }}/>
            <div style={{ fontSize:10.5, color:'#94a3b8', marginTop:3 }}>{hint}</div>
          </div>
        ))}
      </div>
    </Modal>
  );

  // ── Reject modal ─────────────────────────────────────────────
  const RejectModal = ({ cd }) => {
    const [reason, setReason] = useState('');
    return (
      <Modal title="Reject CD Request — Send for Re-inspection"
        onClose={()=>setRejectModal(null)}
        actions={
          <Btn label="Confirm Rejection" color={T.danger}
            onClick={()=>{ updateCD(cd,'Rejected',{},reason||'Rejected by manager'); setRejectModal(null); }}/>
        }>
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:11, fontWeight:700, color:T.textSub, display:'block', marginBottom:4 }}>
            Rejection Reason / Instructions to QA
          </label>
          <textarea value={reason} rows={3}
            onChange={e=>setReason(e.target.value)}
            placeholder="e.g. Critical defects cannot be commercially approved. Reject the shipment and request re-inspection after rework."
            style={{ width:'100%', padding:'8px 10px', border:`1.5px solid ${T.border}`,
              borderRadius:7, fontSize:12.5, resize:'vertical', boxSizing:'border-box' }}/>
        </div>
      </Modal>
    );
  };

  const TABS = ['PENDING','ESCALATED','APPROVED (CD)','REJECTED','SUMMARY'];

  return (
    <div style={{ padding:'22px 24px 60px', fontFamily:"'Inter',sans-serif" }}>
      {/* Page header */}
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:20 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:4, height:22, borderRadius:2, flexShrink:0,
              background:'linear-gradient(180deg,#dc2626,#f59e0b)' }}/>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
              fontSize:18, fontWeight:800, color:'#0a0e1a' }}>
              COMMERCIAL DECISIONS
            </div>
          </div>
          <div style={{ fontSize:11.5, color:T.textSub, marginTop:2, marginLeft:14 }}>
            Inspection exemptions approved for commercial reasons · Full audit trail
          </div>
        </div>
        {isManager(currentUser) && (
          <Btn label="⚙ Set CD Thresholds" outline color={T.primary}
            onClick={()=>setThresholdModal(true)}/>
        )}
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab}/>

      {/* Pending */}
      {tab==='PENDING' && (
        <div>
          {pending.length===0 ? (
            <div style={{ textAlign:'center', padding:60, color:T.textSub }}>
              <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
              <div style={{ fontWeight:700 }}>No pending commercial decisions</div>
            </div>
          ) : pending.map(cd => cd._isGateException ? (
            // Gate exception card (process flow blocked)
            <div key={cd.id} style={{ background:'white', border:`1.5px solid ${T.warning}`,
              borderRadius:12, overflow:'hidden', marginBottom:14 }}>
              <div style={{ background:'linear-gradient(135deg,#78350f,#92400e)',
                padding:'12px 18px', color:'white',
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:900, fontSize:14 }}>
                    ⚠ Process Flow Exception · PO {cd.poNo}
                  </div>
                  <div style={{ fontSize:11.5, opacity:0.75, marginTop:2 }}>
                    {cd.supplier} · Stage: {cd.stage} · Raised by {cd.raisedBy} on {cd.raisedOn}
                  </div>
                </div>
                <CDPill status="Pending QA Manager"/>
              </div>
              <div style={{ padding:'12px 18px' }}>
                <div style={{ background:'#fffbeb', border:'1px solid #fde68a',
                  borderRadius:8, padding:'10px 12px', marginBottom:12,
                  fontSize:12.5, color:'#92400e' }}>
                  {cd.detail}
                </div>
                {isManager(currentUser) && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <Btn label="✓ Approve Exception" color={T.success} small
                      onClick={async()=>{
                        const upd = {...cd, status:'Approved',
                          approvedBy:currentUser.name, approvedAt:new Date().toISOString()};
                        // Update in db.exceptions
                        setDb(p=>({...p, exceptions:(p.exceptions||[]).map(e=>e.id===cd.id?upd:e)}));
                        await saveToDB('exception', upd);
                        notify('Exception approved');
                      }}/>
                    <Btn label="✕ Reject" color={T.danger} small
                      onClick={async()=>{
                        const upd = {...cd, status:'Rejected',
                          rejectedBy:currentUser.name, rejectedAt:new Date().toISOString()};
                        setDb(p=>({...p, exceptions:(p.exceptions||[]).map(e=>e.id===cd.id?upd:e)}));
                        await saveToDB('exception', upd);
                        notify('Exception rejected','warn');
                      }}/>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <CDDetail key={cd.id} cd={cd}/>
          ))}
        </div>
      )}

      {/* Escalated to merchant */}
      {tab==='ESCALATED' && (
        <div>
          {escalated.length===0 ? (
            <div style={{ textAlign:'center', padding:60, color:T.textSub }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📤</div>
              <div style={{ fontWeight:700 }}>No cases currently with merchants</div>
            </div>
          ) : escalated.map(cd=><CDDetail key={cd.id} cd={cd}/>)}
        </div>
      )}

      {/* Approved */}
      {tab==='APPROVED (CD)' && (
        <div>
          {approved.map(cd=>(
            <div key={cd.id} style={{ background:'white', border:'1px solid #86efac',
              borderRadius:10, padding:'12px 16px', marginBottom:10,
              borderLeft:'4px solid #059669' }}>
              <div style={{ display:'flex', justifyContent:'space-between',
                flexWrap:'wrap', gap:8 }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:13 }}>
                    PO {cd.poNo} · {cd.styleNo} · {cd.supplier}
                  </div>
                  <div style={{ fontSize:11.5, color:T.textSub, marginTop:2 }}>
                    {cd.stage} · {cd.reason?.split('—')[0]} · {cd.raisedOn}
                  </div>
                  <div style={{ fontSize:11.5, marginTop:3 }}>
                    QA: {cd.qaName} · Merchant: {cd.merchantName||'—'}
                    · Qty: {num(cd.qty).toLocaleString()} pcs
                    · {cd.currency} {parseFloat(cd.orderValue||0).toLocaleString()}
                  </div>
                </div>
                <CDPill status={cd.status}/>
              </div>
            </div>
          ))}
          {approved.length===0 && (
            <div style={{ textAlign:'center', padding:60, color:T.textSub }}>
              No commercially approved orders yet
            </div>
          )}
        </div>
      )}

      {/* Rejected */}
      {tab==='REJECTED' && (
        <DataTable
          columns={['Date','PO','Style','Supplier','Stage','Reason','Rejected By']}
          rows={rejected}
          renderCell={(r,c)=>({
            Date:r.raisedOn, PO:<b>{r.poNo}</b>, Style:r.styleNo,
            Supplier:r.supplier, Stage:r.stage,
            Reason:<span style={{fontSize:11}}>{r.reason}</span>,
            'Rejected By':r.timeline?.find(t=>t.action==='Rejected')?.by||'—',
          }[c])}/>
      )}

      {/* Summary */}
      {tab==='SUMMARY' && <CDSummary/>}

      {thresholdModal && <ThresholdModal/>}
      {rejectModal    && <RejectModal cd={rejectModal}/>}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
}
